import { Helmet } from "react-helmet";

import formatPageTitle from "@utils/formatPageTitle";
import { Stack, Typography } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useFormWithNotification } from "@covertree/hooks";

import { BulkQuotesForm, defaultValues } from "./form";
import { MAX_FILE_SIZE } from "@consts/files";
import FormFileField from "@covertree/ui/forms/FormFileField";
import { Button, Form } from "@covertree/ui";
import { FormProvider } from "react-hook-form";
import { useCallback, useState } from "react";
import DocumentItem from "@covertree/ui/content/DocumentItem";
import { useUploadBulkQuotesMutation } from "@redux/restApi/bulkQuotesApi";
import { Toast } from "@covertree/utils";

export function BulkQuotes() {
  const [isLoading, setIsLoading] = useState(false);

  const [uploadBulkQuotes] = useUploadBulkQuotesMutation();

  const form = useFormWithNotification<BulkQuotesForm>({
    defaultValues,
  });

  const { handleSubmit: submit, watch, reset: resetForm } = form;

  const files = watch("files") || [];

  const handleSubmit = useCallback(
    async (payload: BulkQuotesForm) => {
      setIsLoading(true);

      const result = await uploadBulkQuotes({ file: payload.files[0] });

      if (result?.error) {
        Toast.showError("Could not upload bulk quotes file");
      } else {
        Toast.showSuccess("Bulk quotes file uploaded successfully");
        resetForm();
      }

      setIsLoading(false);
    },
    [resetForm, uploadBulkQuotes]
  );

  return (
    <>
      <Helmet>
        <title>{formatPageTitle("Create Bulk Quotes")}</title>
      </Helmet>
      <Typography variant="h3" gutterBottom>
        Create Bulk Quotes
      </Typography>
      <Typography gutterBottom variant="h6">
        You can create multiple quotes by uploading the needed data. The allowed
        file type is a .csv. Any failure reports will be sent to you via e-mail.
        The process will start automatically just after the file will be
        uploaded successfully. If the number of created quotes is high it might
        take a while for all of them to be created. All created quotes will be
        visible on your Quotes page.
      </Typography>
      <Form onSubmit={submit(handleSubmit)}>
        <FormProvider {...form}>
          <Stack direction="column" spacing={2}>
            <FormFileField
              name="files"
              accept={{
                "text/csv": [".csv"],
              }}
              maxFileSize={MAX_FILE_SIZE}
              maxFileAmount={1}
              disabled={isLoading}
            />
            {files.map((file: File, index) => (
              <DocumentItem
                key={`${file.name}-${index}`}
                name={file.name}
                actions={[
                  {
                    name: "Remove",
                    onClick: resetForm,
                    isLoading,
                    icon: <Delete />,
                  },
                ]}
              />
            ))}
            <Button
              variant="contained"
              type="submit"
              loading={isLoading}
              disabled={!files.length || isLoading}>
              Submit
            </Button>
          </Stack>
        </FormProvider>
      </Form>
    </>
  );
}
